import { Box, Grid, Typography, Skeleton } from "@mui/material";
import { getShortLabel } from "../../helpers/helper";
import { useTerritorySalesMetrics } from "../../hooks/useTerritorySalesMetrics";
import { format2 } from "../../helpers/helper";
import { useMemo } from "react";

export default function SalesTerritoryWiseTable({
  dateParams,
  territories = [],
  selectedTerritory,
  onTerritoryChange,
  isDateRangeIncomplete,
}) {
  const year = dateParams?.from ? dateParams.from.slice(0, 4) : new Date().getFullYear();

  const normalizedTerritoryId = selectedTerritory ? String(selectedTerritory) : "";

  const sortedTerritories = useMemo(() => {
    return [...territories].sort((a, b) => {
      return a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' });
    });
  }, [territories]);

  const { data, isLoading, error } = useTerritorySalesMetrics({
    territoryId: normalizedTerritoryId,
    dateParams,
    year,
    isDateRangeIncomplete,
  });

  const safeData = data || {};

  const hasData =
    (safeData.salesRetail > 0) ||
    (safeData.salesProject > 0) ||
    (safeData.budgetRetail > 0) ||
    (safeData.budgetProject > 0);
    
  if (isDateRangeIncomplete) {
    return (
      <Box
        sx={{
          minHeight: 294,
          p: { xs: 2.5, lg: 2 },
          borderRadius: "20px",
          bgcolor: "background.paper",
          boxShadow: "0px 6px 20px rgba(0,0,0,0.35)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography sx={{ color: "warning.main", fontSize: 12 }}>
          Select both From & To dates
        </Typography>
      </Box>
    );
  }

  if (isLoading && selectedTerritory) {
    return (
      <Box
        sx={{
          minHeight: 294,
          p: { xs: 2.5, lg: 2 },
          borderRadius: "20px",
          bgcolor: "background.paper",
          boxShadow: "0px 6px 20px rgba(0,0,0,0.35)",
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {[1, 2, 3].map(i => (
            <Skeleton key={i} variant="rectangular" height={32} />
          ))}
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          minHeight: 294,
          p: { xs: 2.5, lg: 2 },
          borderRadius: "20px",
          bgcolor: "background.paper",
          boxShadow: "0px 6px 20px rgba(0,0,0,0.35)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography sx={{ color: "error.main", fontSize: 12 }}>
          {error}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: 294,
        p: { xs: 2.5, lg: 2 },
        borderRadius: "20px",
        bgcolor: "background.paper",
        boxShadow: "0px 6px 20px rgba(0,0,0,0.35)",
        display: "flex",
        flexDirection: "column",
        transition: "all 0.2s ease",
        "&:hover": { transform: "translateY(-2px)" },
      }}
    >
      <Typography sx={{ fontSize: { xs: 12, lg: 14 }, color: "secondary.main", mb: 2 }}>
        Sales {year} – Territory wise
      </Typography>

      <Box sx={{ display: "flex", gap: 1, mb: 5, overflowX: "auto" }}>
        {sortedTerritories.map(territory => {
          const active = territory.id === selectedTerritory;

          return (
            <Box
              key={territory.id}
              onClick={() => onTerritoryChange(territory.id)}
              sx={{
                px: 1.5,
                py: "4px",
                fontSize: 12,
                whiteSpace: "nowrap",
                borderRadius: "999px",
                cursor: "pointer",
                border: 1,
                borderColor: active ? "secondary.main" : "divider",
                color: active ? "secondary.main" : "text.secondary",
              }}
            >
              {getShortLabel(territory.name)}
            </Box>
          );
        })}
      </Box>
      {!hasData ? (
        <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Typography sx={{ color: "text.secondary", fontSize: 12 }}>
            No territory data available
          </Typography>
        </Box>
      ) : (

        <Grid container spacing={2} sx={{ flex: 1 }}>
          <Grid item xs={12} md={7}>
            <Box sx={{ overflowX: { xs: "auto", md: "visible" } }}>
              <Grid container spacing={5} sx={{ minWidth: { xs: 360, md: "auto" } }}>
                <Grid item xs={3} />
                <Header xs={3}>RETAIL</Header>
                <Header xs={3}>PROJECT</Header>
                <Header xs={3}>TOTAL</Header>

                <RowLabel xs={3}>SALES</RowLabel>
                <Value xs={3}>{format2(safeData.salesRetail)}</Value>
                <Value xs={3}>{format2(safeData.salesProject)}</Value>
                <Value xs={3}>{format2(safeData.salesTotal)}</Value>

                <RowLabel xs={3}>BUDGET</RowLabel>
                <Value xs={3}>{format2(safeData.budgetRetail)}</Value>
                <Value xs={3}>{format2(safeData.budgetProject)}</Value>
                <Value xs={3}>{format2(safeData.budgetTotal)}</Value>
              </Grid>
            </Box>
          </Grid>

          <Grid
            item
            xs={12}
            md={5}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              flexWrap: { xs: "wrap", md: "nowrap" },
              rowGap: { xs: 2, md: 0 },
            }}
          >
            <Kpi
              label={
                <>
                  ACTUAL<br />
                  VS BUDGET
                </>
              }
              value={
                typeof safeData.actualVsBudget === "number"
                  ? `${format2(safeData.actualVsBudget)} %`
                  : "--"
              }
            />

            <Kpi
              label={
                <>
                  ACTUAL<br />
                  VS PRO RATA
                </>
              }
              value={
                typeof safeData.actualVsProRata === "number"
                  ? `${format2(safeData.actualVsProRata)} %`
                  : "--"
              }
            />

            <Kpi
              label={
                <>
                  SALES<br />
                  SHARE
                </>
              }
              value={
                typeof safeData.salesShare === "number"
                  ? `${format2(safeData.salesShare)} %`
                  : "--"
              }
            />
          </Grid>
        </Grid>
        )}
    </Box>
  );
}

const Header = ({ xs, children }) => (
  <Grid item xs={xs}>
    <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
      {children}
    </Typography>
  </Grid>
);

const RowLabel = ({ xs, children }) => (
  <Grid item xs={xs}>
    <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
      {children}
    </Typography>
  </Grid>
);

const Value = ({ xs, children }) => (
  <Grid item xs={xs}>
    <Typography sx={{ fontSize: 12, color: "text.primary" }}>
      {children ?? "--"}
    </Typography>
  </Grid>
);

const Kpi = ({ label, value }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateRows: "auto 1fr",
      alignItems: "center",
      justifyItems: "center",
      flex: "1 1 0",
      minWidth: 0,
    }}
  >
    <Typography
      sx={{
        fontSize: 12,
        letterSpacing: 0.85,
        color: "text.secondary",
        textAlign: "center",
      }}
    >
      {label}
    </Typography>

    <Typography sx={{ fontSize: 16, color: "text.primary" }}>
      {value ?? "--"}
    </Typography>
  </Box>
);